import React, { useEffect, useRef, useState } from 'react';

/**
 * LowAudioWarning — popup shown when the microphone stays low/muted for a
 * sustained stretch while actively recording.
 *
 * The level indicator (MicControl) already shows the instantaneous low/mute
 * state; this dialog catches the case where it STAYS that way — typically the
 * OS is attenuating the mic (e.g. Windows "Audio enhancements" set to
 * Device Default Effects, which Firefox does not bypass) — so the user finds
 * out seconds in, not after dictating a whole visit into near-silence.
 *
 * Behavior:
 *   - Arms after `thresholdSeconds` of CONTINUOUS mute while recording.
 *   - Dismissible; re-arms only after the audio recovers, so it never nags
 *     every N seconds during one quiet stretch.
 *
 * Styling mirrors the app's existing warning dialogs (short-recording /
 * active-recording modals in App.js).
 */
export default function LowAudioWarning({ isRecording, isMuted, thresholdSeconds = 10 }) {
    const [visible, setVisible] = useState(false);
    // True after the user dismisses, until audio recovers — blocks re-showing.
    const dismissedRef = useRef(false);
    const timerRef = useRef(null);

    useEffect(() => {
        const clear = () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };

        if (isRecording && isMuted) {
            if (!dismissedRef.current && !timerRef.current) {
                timerRef.current = setTimeout(() => {
                    timerRef.current = null;
                    setVisible(true);
                }, thresholdSeconds * 1000);
            }
        } else {
            // Audio recovered (or recording stopped): reset everything so a
            // future quiet stretch warns again.
            clear();
            dismissedRef.current = false;
            setVisible(false);
        }
        return clear;
    }, [isRecording, isMuted, thresholdSeconds]);

    if (!visible) return null;

    return (
        <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000,
        }}>
            <div className="low-audio__card" style={{
                background: '#fff', borderRadius: 16, padding: '32px 28px',
                maxWidth: 460, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            }}>
                <h2 className="low-audio__title" style={{ margin: '0 0 14px', fontSize: 20, fontWeight: 700, color: '#111827' }}>
                    Microphone level is very low
                </h2>
                <p className="low-audio__text" style={{ margin: '0 0 12px', fontSize: 15, color: '#374151', lineHeight: 1.6 }}>
                    We are barely picking up any audio, so the note may come out
                    empty. Please check:
                </p>
                <ul className="low-audio__text" style={{ margin: '0 0 24px', paddingLeft: 20, fontSize: 14, color: '#374151', lineHeight: 1.7 }}>
                    <li>You are speaking near the selected microphone (see the mic picker above).</li>
                    <li>The mic isn't muted by a hardware switch or the OS.</li>
                    <li>
                        On Windows (especially with Firefox): Settings → System → Sound →
                        your microphone → Properties → set <strong>Audio enhancements</strong> to{' '}
                        <strong>Off</strong> or <strong>Voice Clarity</strong> — the
                        "Device Default Effects" mode can heavily reduce the recorded level.
                    </li>
                </ul>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                        onClick={() => { dismissedRef.current = true; setVisible(false); }}
                        className="low-audio__btn"
                        style={{
                            padding: '10px 32px', borderRadius: 8, border: 'none',
                            background: '#1e293b', color: '#fff', fontWeight: 600,
                            fontSize: 14, cursor: 'pointer',
                        }}
                    >
                        Keep recording
                    </button>
                </div>
            </div>
        </div>
    );
}
